import { useState } from "react";
import { ArrowUpRight, Target, PenLine, Layers, RefreshCw, Languages, ShieldCheck, Lightbulb } from "lucide-react";
import { usePal, PalHead, SubHead, Pill, FOCUS } from "./PalUI";
import { resolveIcon } from "../../shared/iconMap";
import OverlayWrapper from "../../shared/OverlayWrapper";

const FIELDS = [
  ["purpose", Target], ["treatment", PenLine], ["options", Layers],
  ["refresh", RefreshCw], ["materials", Languages], ["rights", ShieldCheck],
];

const AreaDetail = ({ area, labels, onClose }) => {
  const { isDark, airline } = usePal();
  return (
    <OverlayWrapper isDark={isDark} onClose={onClose} icon={resolveIcon(area.iconName)} iconColor={area.color} title={area.title} subtitle={`Programming framework, ${airline.name}`} widthClass="max-w-6xl">
      <div className="p-6 grid grid-cols-1 md:grid-cols-[minmax(240px,1fr)_2fr] gap-5" data-testid={`pal-area-detail-${area.id}`}>
        {area.image && (
          <div className="relative h-56 md:h-[262px] md:self-start rounded-2xl overflow-hidden">
            <img src={area.image} alt="" className="absolute inset-0 w-full h-full object-cover" data-testid={`pal-area-image-${area.id}`} />
          </div>
        )}
      <dl className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {FIELDS.map(([f, Icon]) => (
          <div key={f} className={`rounded-2xl border p-4 ${isDark ? "border-white/10 bg-white/[0.03]" : "border-black/10 bg-black/[0.02]"}`}>
            <Icon className="w-5 h-5" style={{ color: area.color }} aria-hidden="true" />
            <dt className={`mt-3 text-[11px] font-semibold uppercase tracking-wider ${isDark ? "text-white/55" : "text-black/55"}`}>{labels[f]}</dt>
            <dd className={`mt-1 text-[14px] leading-snug font-medium ${isDark ? "text-white" : "text-[#1d1d1f]"}`}>{area[f]}</dd>
          </div>
        ))}
      </dl>
      </div>
    </OverlayWrapper>
  );
};

const Storyboard = ({ k, onClose }) => {
  const { isDark, brand } = usePal();
  return (
    <OverlayWrapper isDark={isDark} onClose={onClose} icon={Lightbulb} iconColor={brand} title={k.storyboardTitle} subtitle={k.storyboardLabel} widthClass="max-w-6xl">
      <ol className="p-6 grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4" data-testid="pal-storyboard">
        {k.storyboard.map((s, i) => (
          <li key={s.step}>
            <span className="text-[13px] font-semibold tabular-nums" style={{ color: brand }}>{i + 1}</span>
            <div className={`mt-1 text-[15px] font-semibold ${isDark ? "text-white" : "text-[#1d1d1f]"}`}>{s.step}</div>
            <div className={`mt-1 text-[13px] leading-snug ${isDark ? "text-white/65" : "text-black/60"}`}>{s.desc}</div>
          </li>
        ))}
      </ol>
    </OverlayWrapper>
  );
};

const PalContent = () => {
  const { c, brand, ink, muted, hair, surface } = usePal();
  const k = c.content;
  const [openId, setOpenId] = useState(null);
  const [story, setStory] = useState(false);
  const open = k.areas.find((a) => a.id === openId);
  const storyBtn = (
    <button type="button" onClick={() => setStory(true)} data-testid="pal-storyboard-btn" aria-haspopup="dialog"
      className={`inline-flex items-center gap-2 h-10 px-4 rounded-full border text-[13px] font-semibold ${hair} ${ink} ${FOCUS}`}>
      <Lightbulb className="w-4 h-4" style={{ color: brand }} aria-hidden="true" /> {k.storyboardLabel}
    </button>
  );
  return (
    <section aria-label={k.eyebrow} data-testid="pal-content">
      <PalHead eyebrow={k.eyebrow} title={k.title} accentLine={k.accentLine} lead={k.lead} action={storyBtn} wide />
      <ul className={`mt-10 pt-8 border-t grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 ${hair}`}>
        {k.areas.map((a) => {
          const Icon = resolveIcon(a.iconName);
          return (
            <li key={a.id}>
              <button type="button" onClick={() => setOpenId(a.id)} data-testid={`pal-area-${a.id}`} aria-haspopup="dialog"
                className={`w-full h-full text-left rounded-2xl border p-5 transition-transform hover:-translate-y-0.5 flex items-start gap-4 ${surface} ${FOCUS}`}>
                <span className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${a.color}22` }}>
                  <Icon className="w-4 h-4" style={{ color: a.color }} aria-hidden="true" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className={`block text-[16px] font-semibold leading-tight ${ink}`}>{a.title}</span>
                  <span className={`block mt-1 text-[13.5px] leading-snug ${muted}`}>{a.short}</span>
                </span>
                <ArrowUpRight className="w-4 h-4 flex-shrink-0" style={{ color: a.color }} aria-hidden="true" />
              </button>
            </li>
          );
        })}
      </ul>
      <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3" data-testid="pal-configurations">
        <SubHead>{k.configurationsTitle}</SubHead>
        {k.configurations.map((x) => (
          <span key={x.label} className={`flex items-center gap-2 text-[14px] ${muted}`}>
            <Pill color={brand}>{x.label}</Pill>{x.desc}
          </span>
        ))}
      </div>
      {open && <AreaDetail area={open} labels={k.fieldLabels} onClose={() => setOpenId(null)} />}
      {story && <Storyboard k={k} onClose={() => setStory(false)} />}
    </section>
  );
};

export default PalContent;
